import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Login as BLLogin } from '../../utilities/BL/Login';
import { SessionTimeoutWarning } from '../Commons/SessionTimeoutWarning';

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth debe usarse dentro de un AuthProvider');
    }
    return context;
};

const getApiBaseUrl = () => {
    const envUrl = process.env.REACT_APP_API_URL;
    if (envUrl) {
        return envUrl.endsWith('/') ? envUrl : `${envUrl}/`;
    }
    return '/api/';
};

/**
 * Decodifica de forma segura la fecha de expiración 'exp' de un token JWT.
 * Retorna milisegundos desde epoch o null.
 */
const parseJwtExp = (jwtToken) => {
    if (!jwtToken || typeof jwtToken !== 'string') return null;
    try {
        const parts = jwtToken.split('.');
        if (parts.length < 2) return null;
        const base64Url = parts[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );
        const parsed = JSON.parse(jsonPayload);
        return parsed && typeof parsed.exp === 'number' ? parsed.exp * 1000 : null;
    } catch {
        return null;
    }
};

// Constantes de temporización y sesión
const WARNING_WINDOW_SECONDS = 30; // Alerta 30 segundos antes de expirar
const MAX_IDLE_RENEWALS = 2; // Máximo 2 renovaciones ante avisos de inactividad
const IDLE_ACTIVITY_THRESHOLD_MS = 10 * 60 * 1000; // 10 minutos sin actividad = inactivo
const ACTIVE_AUTO_RENEWAL_WINDOW_MS = 5 * 60 * 1000; // 5 minutos antes de expirar, si está activo renueva en silencio

export const AuthProvider = ({ children }) => {
    const [token, setToken] = useState(() => {
        try {
            return localStorage.getItem('token') || null;
        } catch {
            return null;
        }
    });

    const [refreshToken, setRefreshToken] = useState(() => {
        try {
            return localStorage.getItem('refreshToken') || null;
        } catch {
            return null;
        }
    });

    const [user, setUser] = useState(() => {
        try {
            const rawUser = localStorage.getItem('user');
            return rawUser ? JSON.parse(rawUser) : null;
        } catch {
            return null;
        }
    });

    const [role, setRole] = useState(() => {
        try {
            const storedRole = localStorage.getItem('role');
            if (storedRole) return storedRole;
            const rawUser = localStorage.getItem('user');
            if (rawUser) {
                const parsed = JSON.parse(rawUser);
                return parsed?.role || null;
            }
            return null;
        } catch {
            return null;
        }
    });

    const [isLoading, setIsLoading] = useState(false);

    // Estados para la alerta responsive de expiración e inactividad
    const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
    const [timeoutCountdown, setTimeoutCountdown] = useState(WARNING_WINDOW_SECONDS);
    const [isSuccessRenewed, setIsSuccessRenewed] = useState(false);
    const [isRenewingSession, setIsRenewingSession] = useState(false);
    const [idleRenewalsCount, setIdleRenewalsCount] = useState(() => {
        try {
            return Number(localStorage.getItem('_intk_idle_renewals') || 0);
        } catch {
            return 0;
        }
    });

    // Refs para registrar actividad del operador sin forzar re-renders continuos
    const lastActivityRef = useRef(Date.now());
    const isWarningDismissedRef = useRef(false);
    const successDismissTimeoutRef = useRef(null);

    const isAuthenticated = Boolean(token);

    const logout = useCallback(async () => {
        const currentRefreshToken = refreshToken || localStorage.getItem('refreshToken');
        try {
            if (currentRefreshToken) {
                const baseUrl = getApiBaseUrl();
                await fetch(`${baseUrl}Authentication/logout`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ refreshToken: currentRefreshToken }),
                });
            }
        } catch (error) {
            console.error('Error al invocar endpoint de logout:', error);
        } finally {
            try {
                localStorage.removeItem('token');
                localStorage.removeItem('refreshToken');
                localStorage.removeItem('role');
                localStorage.removeItem('user');
                localStorage.removeItem('_intk_idle_renewals');
            } catch (err) {
                console.warn('Error al limpiar localStorage:', err);
            }
            setToken(null);
            setRefreshToken(null);
            setUser(null);
            setRole(null);
            setIdleRenewalsCount(0);
            setShowTimeoutWarning(false);
            setIsSuccessRenewed(false);
        }
    }, [refreshToken]);

    const refreshAccessToken = useCallback(async () => {
        const currentRefreshToken = refreshToken || localStorage.getItem('refreshToken');
        if (!currentRefreshToken) {
            await logout();
            return null;
        }

        try {
            const baseUrl = getApiBaseUrl();
            const response = await fetch(`${baseUrl}Authentication/refresh-token`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ refreshToken: currentRefreshToken }),
            });

            if (!response.ok) {
                await logout();
                return null;
            }

            const data = await response.json();
            if (data && data.token) {
                const newToken = data.token;
                const newRefreshToken = data.refreshToken || currentRefreshToken;
                const newUser = data.user || null;
                const newRole = newUser?.role || data.role || (newUser ? newUser.role : null);

                try {
                    localStorage.setItem('token', newToken);
                    if (newRefreshToken) localStorage.setItem('refreshToken', newRefreshToken);
                    if (newUser) localStorage.setItem('user', JSON.stringify(newUser));
                    if (newRole) localStorage.setItem('role', newRole);
                } catch (e) {
                    console.warn('Error guardando token renovado en localStorage:', e);
                }

                setToken(newToken);
                if (newRefreshToken) setRefreshToken(newRefreshToken);
                if (newUser) setUser(newUser);
                if (newRole) setRole(newRole);

                // Reiniciar descarte de advertencia para el nuevo ciclo
                isWarningDismissedRef.current = false;
                setShowTimeoutWarning(false);

                return newToken;
            } else {
                await logout();
                return null;
            }
        } catch (error) {
            console.error('Error durante la renovación de token:', error);
            await logout();
            return null;
        }
    }, [refreshToken, logout]);

    const login = useCallback(async (username, password) => {
        setIsLoading(true);
        try {
            const response = await BLLogin(username, password);
            if (response.success && response.data) {
                const newToken = response.data.token;
                const newRefreshToken = response.data.refreshToken;
                const newUser = response.data.user;
                const newRole = newUser?.role || response.data.role || '';

                setToken(newToken);
                setRefreshToken(newRefreshToken);
                setUser(newUser);
                setRole(newRole);
                setIdleRenewalsCount(0);
                lastActivityRef.current = Date.now();
                isWarningDismissedRef.current = false;

                try {
                    if (newToken) localStorage.setItem('token', newToken);
                    if (newRefreshToken) localStorage.setItem('refreshToken', newRefreshToken);
                    if (newUser) localStorage.setItem('user', JSON.stringify(newUser));
                    if (newRole) localStorage.setItem('role', newRole);
                    localStorage.setItem('_intk_idle_renewals', '0');
                } catch (e) {
                    console.warn('Error guardando sesión:', e);
                }
            }
            return response;
        } finally {
            setIsLoading(false);
        }
    }, []);

    const authenticatedFetch = useCallback(async (url, options = {}) => {
        const currentToken = token || localStorage.getItem('token');
        const headers = new Headers(options.headers || {});

        if (!headers.has('Content-Type') && !(options.body instanceof FormData)) {
            headers.set('Content-Type', 'application/json');
        }

        if (currentToken) {
            headers.set('Authorization', `Bearer ${currentToken}`);
        }

        const fetchOptions = {
            ...options,
            headers,
        };

        let response = await fetch(url, fetchOptions);

        if (response.status === 401) {
            const newToken = await refreshAccessToken();
            if (newToken) {
                const retryHeaders = new Headers(options.headers || {});
                if (!retryHeaders.has('Content-Type') && !(options.body instanceof FormData)) {
                    retryHeaders.set('Content-Type', 'application/json');
                }
                retryHeaders.set('Authorization', `Bearer ${newToken}`);
                response = await fetch(url, {
                    ...options,
                    headers: retryHeaders,
                });
            } else {
                await logout();
            }
        }

        return response;
    }, [token, refreshAccessToken, logout]);

    const hasRole = useCallback((allowedRoles) => {
        if (!allowedRoles || (Array.isArray(allowedRoles) && allowedRoles.length === 0)) {
            return true;
        }
        if (!role) {
            return false;
        }
        const rolesArray = Array.isArray(allowedRoles) ? allowedRoles : [allowedRoles];
        const normalizedCurrentRole = role.toString().trim().toLowerCase();
        return rolesArray.some((r) => r.toString().trim().toLowerCase() === normalizedCurrentRole);
    }, [role]);

    // Registrar actividad del usuario (mouse, teclado, touch, scroll)
    useEffect(() => {
        if (!isAuthenticated) return;

        let lastThrottle = 0;
        const handleUserActivity = () => {
            const now = Date.now();
            if (now - lastThrottle > 3000) {
                lastThrottle = now;
                lastActivityRef.current = now;
            }
        };

        const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
        events.forEach((ev) => window.addEventListener(ev, handleUserActivity, { passive: true }));

        return () => {
            events.forEach((ev) => window.removeEventListener(ev, handleUserActivity));
        };
    }, [isAuthenticated]);

    // Timer de expiración de token y detección de inactividad
    useEffect(() => {
        if (!isAuthenticated || !token) {
            setShowTimeoutWarning(false);
            return;
        }

        const interval = setInterval(async () => {
            const tokenExpMs = parseJwtExp(token);
            if (!tokenExpMs) return;

            const now = Date.now();
            const timeLeftMs = tokenExpMs - now;
            const isUserActive = (now - lastActivityRef.current) < IDLE_ACTIVITY_THRESHOLD_MS;

            // CASO 1: El operador está activo en el estadio
            if (isUserActive) {
                // Si el token está a punto de vencer (menos de 5 min) y el usuario está trabajando,
                // renueva silenciosamente en segundo plano sin interrumpir su trabajo
                if (timeLeftMs > 0 && timeLeftMs <= ACTIVE_AUTO_RENEWAL_WINDOW_MS && !isRenewingSession) {
                    await refreshAccessToken();
                    setShowTimeoutWarning(false);
                }
                return;
            }

            // CASO 2: El operador dejó la pantalla desatendida / inactiva
            if (!isUserActive) {
                // Alerta 30 segundos antes de expirar
                if (timeLeftMs > 0 && timeLeftMs <= WARNING_WINDOW_SECONDS * 1000) {
                    const secondsLeft = Math.max(1, Math.ceil(timeLeftMs / 1000));
                    setTimeoutCountdown(secondsLeft);

                    if (idleRenewalsCount < MAX_IDLE_RENEWALS) {
                        if (!isWarningDismissedRef.current) {
                            setShowTimeoutWarning(true);
                        }
                    } else {
                        // Si ya usó las 2 renovaciones por inactividad, no muestra aviso
                        setShowTimeoutWarning(false);
                    }
                }

                // Si el tiempo de vida terminó por completo
                if (timeLeftMs <= 0) {
                    clearInterval(interval);
                    setShowTimeoutWarning(false);

                    let expireReason = 'Tu sesión ha finalizado por inactividad. Por favor, ingresa nuevamente.';
                    if (idleRenewalsCount >= MAX_IDLE_RENEWALS) {
                        expireReason = 'Tu sesión finalizó por inactividad prolongada (límite alcanzado). Por seguridad, ingresa nuevamente.';
                    }

                    try {
                        sessionStorage.setItem('_session_expired_msg', expireReason);
                    } catch (e) {
                        console.warn('No se pudo guardar mensaje de expiración:', e);
                    }

                    await logout();
                    window.location.href = '/login';
                }
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [isAuthenticated, token, idleRenewalsCount, isRenewingSession, refreshAccessToken, logout]);

    // Acción al presionar "Mantener abierta"
    const handleKeepAlive = async () => {
        setIsRenewingSession(true);
        try {
            const newToken = await refreshAccessToken();
            if (newToken) {
                setIdleRenewalsCount((prev) => {
                    const next = prev + 1;
                    try {
                        localStorage.setItem('_intk_idle_renewals', String(next));
                    } catch {}
                    return next;
                });

                lastActivityRef.current = Date.now();
                isWarningDismissedRef.current = false;
                setShowTimeoutWarning(false);

                // Mostrar notificación de éxito por 3 segundos
                setIsSuccessRenewed(true);
                if (successDismissTimeoutRef.current) {
                    clearTimeout(successDismissTimeoutRef.current);
                }
                successDismissTimeoutRef.current = setTimeout(() => {
                    setIsSuccessRenewed(false);
                }, 3000);
            }
        } finally {
            setIsRenewingSession(false);
        }
    };

    // Botón 'X' para cerrar aviso de advertencia (cierra el mensaje, NO renueva la sesión)
    const handleCloseWarning = () => {
        setShowTimeoutWarning(false);
        isWarningDismissedRef.current = true;
    };

    // Botón 'X' para cerrar aviso de éxito
    const handleCloseSuccess = () => {
        setIsSuccessRenewed(false);
        if (successDismissTimeoutRef.current) {
            clearTimeout(successDismissTimeoutRef.current);
        }
    };

    const value = useMemo(() => ({
        user,
        token,
        refreshToken,
        role,
        isAuthenticated,
        isLoading,
        login,
        logout,
        refreshAccessToken,
        authenticatedFetch,
        hasRole,
    }), [user, token, refreshToken, role, isAuthenticated, isLoading, login, logout, refreshAccessToken, authenticatedFetch, hasRole]);

    return (
        <AuthContext.Provider value={value}>
            {children}
            {isAuthenticated && (
                <SessionTimeoutWarning
                    show={showTimeoutWarning}
                    countdown={timeoutCountdown}
                    onKeepAlive={handleKeepAlive}
                    onClose={handleCloseWarning}
                    isSuccess={isSuccessRenewed}
                    onCloseSuccess={handleCloseSuccess}
                    isRenewing={isRenewingSession}
                />
            )}
        </AuthContext.Provider>
    );
};

export default AuthProvider;
